import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, CheckCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const features = [
    'Networking Infrastructure',
    'Cybersecurity Services',
    'Audio Visual Systems',
    'Integrated Security Solutions',
    'Cloud Migration & Management',
    'Digital Transformation',
    'Custom Software Development',
    '24/7 Technical Support'
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden hero-bg">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float animation-delay-400"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-128 h-128 bg-gradient-radial from-primary/10 to-transparent rounded-full"></div>
      </div>

      <div className="relative max-w-7xl mx-auto container-padding py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className="space-y-8 animate-slide-up">
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Powerful & Flexible
                <span className="block text-gradient">Technology Solutions</span>
              </h1>
              <p className="text-xl text-secondary-200 max-w-2xl">
                Securing Futures with Robust Cybersecurity, Seamless Networking, and Integrated AV Systems.
              </p>
            </div>

            {/* Features List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div 
                  key={feature} 
                  className="flex items-center space-x-3 animate-fade-in"
                  style={{ animationDelay: `${index * 200}ms` }}
                >
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                  <span className="text-secondary-100">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button variant="accent" size="lg" className="group" asChild>
                <Link to="/contact">
                  Get Started Today
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              
              <Button variant="ghost" size="lg" className="hidden text-white border-white hover:bg-white/10" asChild>
                <Link to="/portfolio" className="flex items-center">
                  <Play className="w-5 h-5 mr-2" />
                  View Our Work
                </Link>
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 pt-8 border-t border-white/20">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">500+</div>
                <div className="text-sm text-secondary-200">Projects Completed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">10+</div>
                <div className="text-sm text-secondary-200">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">99%</div>
                <div className="text-sm text-secondary-200">Client Satisfaction</div>
              </div>
            </div>
          </div>

          {/* Hero Image/Graphics */}
          <div className="relative animate-fade-in animation-delay-400">
            <div className="relative z-10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              {/* Tech Stack Visual */}
              <div className="space-y-6">
                <div className="text-center text-white font-semibold text-lg mb-8">
                  Our Technology Stack
                </div>
                
                {/* Tech Icons Grid */}
                <div className="grid grid-cols-4 gap-6">
                  {[
                    'Firewalls','IDS','IPS','VPN',
                     'CCTV','React', 'AWS',
                    , 'Azure'
                  ].map((tech, index) => (
                    <div 
                      key={tech}
                      className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center hover:bg-white/20 transition-all duration-300 hover:scale-105"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="w-8 h-8 bg-gradient-tech rounded mx-auto mb-2"></div>
                      <div className="text-xs text-white font-medium">{tech}</div>
                    </div>
                  ))}
                </div>

                {/* Animated Code Block */}
                <div className="bg-slate-900/50 rounded-lg p-4 font-data text-sm text-green-400 overflow-hidden">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                  <div className="space-y-1">
                    <div className="animate-pulse">const solution = await buildApp()</div>
                    <div className="animate-pulse animation-delay-200">console.log('🚀 Deployed successfully!')</div>
                    <div className="animate-pulse animation-delay-400">// Your business transformed ✨</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-accent/30 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary/30 rounded-full blur-xl animate-pulse animation-delay-600"></div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
